import type { FaceMap } from '@gymcloud/faces'

import type { LoggedSet } from '~/data/types'

/*
 * The copies a faces step makes as its people are pressed — `/specs/features/faces.spec.md`.
 * Each set's copy for the people now blurred is begun at once, the one under way for anybody
 * else given up; every copy finished is kept for as long as the page that asked stands, so going
 * back to people blurred before, opening the step again, or downloading the same copy twice is
 * the copy made then. One is made at a time. Whatever was made goes when the page does.
 */

/** Where the copy of one set's video stands, for whatever is showing that video. */
export type CopyState =
  | { readonly kind: 'original' }
  | {
      readonly kind: 'making'
      readonly progress: number
      /** The copy last made, which stands while this one is made; null where none was. */
      readonly standing: string | null
    }
  | { readonly kind: 'made'; readonly url: string }
  | { readonly kind: 'failed' }

/** Makes one copy: the set's video with [hidden]'s faces blurred, as filmed where nobody is. */
export type MakeCopy = (
  set: LoggedSet,
  hidden: ReadonlySet<string>,
  map: FaceMap | null,
  options: { onProgress: (progress: number) => void; signal: AbortSignal },
) => Promise<Blob>

const nameOf = (setId: string, hidden: ReadonlySet<string>) =>
  `${setId}|${[...hidden].sort().join(',')}`

export class FacesCopies {
  private readonly make: MakeCopy
  private readonly made = new Map<string, Blob | null>()
  private readonly urls = new Map<Blob, string>()
  private readonly making = new Map<
    string,
    { readonly name: string; readonly abort: AbortController; readonly done: Promise<Blob | null> }
  >()
  private readonly listeners = new Set<() => void>()
  private states = new Map<string, CopyState>()
  // One at a time: each copy waits for the one before it.
  private line: Promise<unknown> = Promise.resolve()

  constructor(make: MakeCopy) {
    this.make = make
  }

  /** Where each set's copy stands, by its id — a new map every time anything moves. */
  get current(): ReadonlyMap<string, CopyState> {
    return this.states
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private set(setId: string, state: CopyState) {
    this.states = new Map(this.states).set(setId, state)
    for (const listener of this.listeners) listener()
  }

  /** The copy standing for [setId] now — the one made, or the one standing while another is. */
  private standing(setId: string): string | null {
    const now = this.states.get(setId)
    if (now?.kind === 'made') return now.url
    if (now?.kind === 'making') return now.standing
    return null
  }

  private stateOf(copy: Blob | null): CopyState {
    if (copy === null) return { kind: 'failed' }
    let url = this.urls.get(copy)
    if (url === undefined) {
      url = URL.createObjectURL(copy)
      this.urls.set(copy, url)
    }
    return { kind: 'made', url }
  }

  private begin(set: LoggedSet, map: FaceMap | null, hidden: ReadonlySet<string>) {
    const name = nameOf(set.id, hidden)
    const abort = new AbortController()
    const done = this.line
      .catch(() => undefined)
      .then(async () => {
        abort.signal.throwIfAborted()
        return this.make(set, hidden, map, {
          signal: abort.signal,
          onProgress: (progress) => {
            if (this.making.get(set.id)?.name === name)
              this.set(set.id, { kind: 'making', progress, standing: this.standing(set.id) })
          },
        })
      })
      .then(
        (copy) => copy,
        (error: unknown) => {
          if (abort.signal.aborted) throw error
          return null
        },
      )
      .then((copy) => {
        // Only a copy made is kept: one that could not be is made again when next asked.
        if (copy !== null) this.made.set(name, copy)
        if (this.making.get(set.id)?.name === name) {
          this.making.delete(set.id)
          this.set(set.id, this.stateOf(copy))
        }
        return copy
      })
    this.line = done.catch(() => undefined)
    this.making.set(set.id, { name, abort, done })
    return done
  }

  /** Begins [set]'s copy with [hidden]'s faces blurred, unless it is made or under way. */
  want(set: LoggedSet, map: FaceMap, hidden: ReadonlySet<string>): void {
    const under = this.making.get(set.id)
    if (hidden.size === 0) {
      under?.abort.abort()
      this.making.delete(set.id)
      this.set(set.id, { kind: 'original' })
      return
    }
    const name = nameOf(set.id, hidden)
    if (this.made.has(name)) {
      under?.abort.abort()
      this.making.delete(set.id)
      this.set(set.id, this.stateOf(this.made.get(name) ?? null))
      return
    }
    if (under?.name === name) return
    under?.abort.abort()
    this.set(set.id, { kind: 'making', progress: 0, standing: this.standing(set.id) })
    void this.begin(set, map, hidden).catch(() => undefined)
  }

  /** [set]'s copy with [hidden]'s faces blurred, waiting for it where it is under way. */
  async handOver(
    set: LoggedSet,
    map: FaceMap | null,
    hidden: ReadonlySet<string>,
  ): Promise<Blob | null> {
    const name = nameOf(set.id, hidden)
    if (this.made.has(name)) return this.made.get(name) ?? null
    const under = this.making.get(set.id)
    if (under?.name === name) return under.done
    if (hidden.size === 0) {
      // The video as filmed is not a copy worth keeping: fetched for the one handing over.
      return this.make(set, hidden, map, {
        signal: new AbortController().signal,
        onProgress: () => undefined,
      }).catch(() => null)
    }
    return this.begin(set, map, hidden)
  }

  /** Gives up whatever is under way and lets every copy go, the page — or its set — being gone. */
  close(): void {
    for (const under of this.making.values()) under.abort.abort()
    this.making.clear()
    for (const url of this.urls.values()) URL.revokeObjectURL(url)
    this.urls.clear()
    this.made.clear()
    this.listeners.clear()
  }
}
